import * as Sentry from '@sentry/browser';
import { isNil, toUpper } from 'lodash';
import qs from 'qs';
import { action, computed, makeObservable, observable, reaction } from 'mobx';

import { permissions } from '@/constants';
import { ActivityLogMock } from '@/constants/ActivityLogMock';
import { DefaultSetpointsOption, ProactiveEngineStateEnum } from '@/constants/enums';
import epaxios from '@/utils/epaxios';
import LogErrorMessage from '@/utils/LogErrorMessage';
import { waitFor } from '@/utils/util-functions';

import RootStore from './RootStore';
import GetCommandsHistoryFilter from './GetCommandsHistoryFilter';
import { EngineDeactivationReasonEnum } from '@/constants/engine-deactivation-reason';
import { LifecycleStageId } from '@/comp/pages/LifecyclePage/constants';

export default class ProactiveEngineStore {
  rootStore: typeof RootStore;
  @observable proactiveEngineState; //activated, deactivated, unavailable
  @observable engineStatus: number;
  @observable cloudControl: boolean;
  @observable engineReady: boolean;
  @observable localStationControl: boolean;
  @observable managedVersion: string;
  @observable commandsHistory = [];
  @observable commandsHistoryFilter = new GetCommandsHistoryFilter();
  @observable isRetrievingState = false;
  @observable isChangingState = false;
  @observable isLoadingCommandsHistory = false;
  @observable defaultSetpointsOption;
  @observable isRetrievingDefaultSetpointsOption = false;
  @observable isNextPeriodicShutdownNear;

  constructor(rootStore: typeof RootStore) {
    makeObservable(this);
    this.rootStore = rootStore;

    reaction(
      () => this.rootStore.stationStore.currentStation.id,
      currentStationId => {
        if (currentStationId) {
          this.reset();
          this.getProactiveEngineState();
          if (this.rootStore.permissionStore.isAuth(permissions.VIEW_STATION_VALUE_DASHBOARD_LEAKAGE))
            this.getIsNextPeriodicShutdownNear();
        }
      },
    );
  }

  @computed get isProactiveEngineEditDisabled() {
    return (
      this.proactiveEngineState === ProactiveEngineStateEnum.Deactivated &&
      (!this.engineReady || this.localStationControl)
    );
  }

  @action getProactiveEngineState = async () => {
    this.isRetrievingState = true;

    try {
      const {
        data,
      }: {
        data: {
          state: ProactiveEngineStateEnum;
          cloudControl: boolean;
          engineReady: boolean;
          localStationControl: boolean;
          managedVersion: string;
        };
      } = await epaxios.get(`/station/${this.rootStore.stationStore.currentStation.id}/proactive-engine`);
      this.proactiveEngineState = data.state;
      this.cloudControl = data.cloudControl;
      this.engineReady = data.engineReady;
      this.localStationControl = data.localStationControl;
      this.managedVersion = data.managedVersion;
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Proactive Engine state: ${e}`));
    } finally {
      this.isRetrievingState = false;
    }
  };

  @action getDefaultSetpointsOption = async () => {
    this.isRetrievingDefaultSetpointsOption = true;
    try {
      const { data }: { data: { defaultSetpointsOption: boolean } } = await epaxios.get(
        `/station/${this.rootStore.stationStore.currentStation.id}/default-setpoints/availability`,
      );
      this.defaultSetpointsOption = data.defaultSetpointsOption;
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Default Setpoints availability: ${e}`));
    } finally {
      this.isRetrievingDefaultSetpointsOption = false;
    }
  };

  @action updateProactiveEngineStateFromServer = newState => {
    if (typeof newState === 'string') {
      this.proactiveEngineState = newState;
    }
  };

  @action updateProactiveEngineStateFromEngineStatus = payload => {
    if (payload != null && payload?.engineStatus != null) {
      this.engineStatus = payload?.engineStatus;
    }
  };

  @action toggleState = async (commentRef: EngineDeactivationReasonEnum, comment: string) => {
    this.isChangingState = true;
    const requiredState = !(toUpper(this.proactiveEngineState) === 'ACTIVATED');
    try {
      if (!this.validateToggleActivation(requiredState)) {
        return;
      }
      const { data }: { data: { state: ProactiveEngineStateEnum } } = await epaxios.put(
        `/station/${this.rootStore.stationStore.currentStation.id}/proactive-engine/cloud-control`,
        {
          value: requiredState,
          commentRef: requiredState ? null : EngineDeactivationReasonEnum[commentRef],
          comment: requiredState ? null : comment,
        },
      );

      this.proactiveEngineState = data.state;

      this.rootStore.stationStore.fetchStationData(this.rootStore.stationStore.currentStation.id);
    } catch (e) {
      LogErrorMessage('Failed changing dynamic controller state ', e);
      return Promise.reject(e);
    } finally {
      this.isChangingState = false;
    }
  };

  private validateToggleActivation(requiredState) {
    if (
      requiredState === true &&
      this.rootStore.stationStore?.currentStationStage?.stage?.id !== LifecycleStageId.Subscription &&
      this.rootStore.stationStore?.currentStationStage?.stage?.id !== LifecycleStageId.ControlPrep
    ) {
      throw new Error('dynamicControlInvalidStationStage');
    }
    return true;
  }

  @action getDefaultSetpoints = async (): Promise<DefaultSetpointsOption> => {
    this.isRetrievingDefaultSetpointsOption = true;
    try {
      const { data }: { data: { defaultSetpointsConfig: { defaultType: DefaultSetpointsOption } } } = await epaxios.get(
        `engine-configurator/station/${this.rootStore.stationStore.currentStation.id}/default-setpoints/summary`,
      );
      return data?.defaultSetpointsConfig?.defaultType;
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Default Setpoints: ${e}`));
      return Promise.reject(e);
    } finally {
      this.isRetrievingDefaultSetpointsOption = false;
    }
  };

  @action setCommandHistoryMockData = () => {
    const data = ActivityLogMock;
    this.modifyCommandHistoryIndicatorName(data);
    this.commandsHistory = data;
  };

  @action modifyCommandHistoryIndicatorName = data => {
    data.forEach(command => {
      if (!isNil(command.indicatorName)) {
        command.indicatorName = command.indicatorName.replace(/_/g, ' ');
        command.indicatorName = command.indicatorName.charAt(0).toUpperCase() + command.indicatorName.slice(1);
      }
    });
  };

  @action getCommandsHistory = async (append = false) => {
    this.isLoadingCommandsHistory = true;
    if (!append) {
      this.commandsHistoryFilter.before = null;
    }
    try {
      await waitFor(() => this.rootStore.stationStore.isLoading === false);
      const data = (
        await epaxios.get(`/ecoboxAPI/insert-data/station/${this.rootStore.stationStore.currentStation.id}`, {
          params: this.commandsHistoryFilter,
          paramsSerializer: {
            serialize: params => {
              return qs.stringify(params, { arrayFormat: 'comma', encode: false });
            },
          },
        })
      )?.data;

      if (Array.isArray(data)) {
        this.modifyCommandHistoryIndicatorName(data);
        this.commandsHistory = append ? this.commandsHistory.concat(data) : data;
      } else {
        return Promise.reject();
      }
    } catch (e) {
      LogErrorMessage('Failed retrieving dynamic controller commands history ', e);
      return Promise.reject(e);
    } finally {
      this.isLoadingCommandsHistory = false;
    }
  };

  @action getIsNextPeriodicShutdownNear = async () => {
    try {
      const { data }: { data: { isNextLeakDetectionPeriodNear: boolean } } = await epaxios.get(
        `value-dashboard/leakage/station/${this.rootStore.stationStore.currentStation.id}/next-leak-detection-period-near`,
      );
      this.isNextPeriodicShutdownNear = data.isNextLeakDetectionPeriodNear;
    } catch (e) {
      Sentry.captureException(new Error(`Error when checking if next periodic shutdown is near: ${e}`));
    }
  };

  @action reset = () => {
    this.proactiveEngineState = null;
    this.commandsHistoryFilter = new GetCommandsHistoryFilter();
    this.managedVersion = null;
  };

  @action setCommandsHistoryFilter = (filters: Partial<GetCommandsHistoryFilter>) => {
    Object.assign(this.commandsHistoryFilter, filters);
  };

  getProactiveEngineStatusLabel(hasViewEnginePermissions: boolean, t) {
    const dynamicControllerLabel = `${t('Dynamic Controller')} ${
      hasViewEnginePermissions ? `${this.managedVersion ?? '1.0'} ` : ''
    }`;
    switch (this.proactiveEngineState) {
      case ProactiveEngineStateEnum.Activated:
        return `${dynamicControllerLabel}${t('is Activated')}`;
      case ProactiveEngineStateEnum.Unavailable:
        return `${dynamicControllerLabel}${t('is Deactivated')}`;
      case ProactiveEngineStateEnum.Deactivated:
        if (!this.engineReady) {
          return `${dynamicControllerLabel}${t('is Disabled - Under Maintenance')}`;
        }
        if (this.cloudControl === false) {
          if (this.localStationControl) {
            return `${dynamicControllerLabel}${t('is Disabled by Local Control Switch')}`;
          }
          return `${dynamicControllerLabel}${t('is Disabled by Cloud Control Switch')}`;
        }
        break;
    }
  }
}
